import { usePrevious } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { InlineSearch } from '@calendly/ui/components/inline-search'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  useLazyFetchMoreEventTypesEventTypesQuery,
  useLazySearchForUserOwnerEventTypesQuery,
} from 'features/event_type_editor/api'
import { useDebounce } from 'hooks/useDebounce'
import { currentOrganizationSelector } from 'selectors/current_organization'

const updateScope = (scope, ownerName, managerCount) => {
  const overrideHint = I18n.t('publisher.availability.schedules.tooltip.edit_override')
  const permissionTeamHint = I18n.t('publisher.availability.schedules.tooltip.managed_by_team')
  const permissionOwnerHint = I18n.t('publisher.availability.schedules.tooltip.managed_by_owner', { owner_name: ownerName })

  return ({
    ...scope,
    eventTypes: scope.eventTypes.map((eventType) => {
      let canEdit = true
      let hint = ''

      if (!eventType.policy.edit) {
        canEdit = false
        hint = managerCount > 1 ? permissionTeamHint : permissionOwnerHint
      } else if (eventType.availabilityOverride) {
        canEdit = false
        hint = overrideHint
      }

      return {
        ...eventType,
        canEdit,
        editLockedHint: hint
      }
    })
  })
}

export const useSearchForEventTypesV4 = ({ eventTypeId, disabled = false, filterLocked = false, userId, addEditInfo = false }) => {
  const [query, updateQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)
  const previousQuery = usePrevious(debouncedQuery)
  const [fetchSearchResults, { data: searchResultData, isFetching: isLoading }] = useLazySearchForUserOwnerEventTypesQuery()
  const [fetchMoreSearchResults, { isFetching: isLoadingMore }] = useLazyFetchMoreEventTypesEventTypesQuery()
  const currentOrganization = useSelector(currentOrganizationSelector)
  const ownerName = currentOrganization?.owner.name
  const managerCount = currentOrganization.managing_members_count

  const scopeParams = useMemo(() => ({
    extraParams: { ...(filterLocked ? { skip_with_locked_sections: ['v3_availability'] } : {}) },
    eventTypeId,
    filterTerm: debouncedQuery,
    userId
  }), [debouncedQuery, eventTypeId, filterLocked, userId])

  useEffect(() => {
    if (previousQuery === '' || debouncedQuery !== previousQuery) {
      fetchSearchResults(scopeParams)
    }
  }, [debouncedQuery, eventTypeId, fetchSearchResults, filterLocked, previousQuery, scopeParams, userId])

  const fetchMore = ({ profileType, profileId, page }) => {
    fetchMoreSearchResults({ ...scopeParams, page, profileType, profileId, scopeParams })
  }

  const searchResults = useMemo(() => {
    if (!searchResultData?.results) { return null }

    const filteredData = searchResultData.results
      .filter((result) => result.eventTypes.length > 0)

    return addEditInfo
      ? filteredData.map((scope) => updateScope(scope, ownerName, managerCount))
      : filteredData
  }, [addEditInfo, managerCount, ownerName, searchResultData])

  return [(
    <InlineSearch
      placeholder={I18n.t('publisher.event_type_editor.sections.available_hours.search_event_types')}
      onChange={updateQuery}
      disabled={disabled}
      value={query}
    />
  ), debouncedQuery, searchResults, isLoading, fetchMore, isLoadingMore]
}
